/* Real 3D cone/tube viewer for the size-chart diagram - Three.js r128 (same
   version/CDN as kingcustomcones.com/cone-builder-2/), replacing the old
   flat SVG illustration. Geometry, drag-to-rotate, scroll-to-zoom and the
   logo/band system are all reimplemented for real 3D:

   - The shape is built from stacked THREE.CylinderGeometry segments (a
     cone is just a cylinder with different top/bottom radii - same trick
     the reference builder uses). Real geometry + real lights means the
     glossy "wrapped" look comes from actual shading, not a hand-painted
     gradient trick.
   - The tip (cones) / band (tubes) segment gets its own canvas texture.
     Because the mesh is genuinely 3D, a flat logo painted onto that
     texture automatically reads as wrapped once the camera/lighting
     render it - no manual per-row taper warp needed anymore.
   - Drag-to-rotate and scroll-to-zoom are hand-rolled (pointer events),
     matching how the reference builder does it (no OrbitControls import
     there either).

   Upload / orientation / toolbar DOM and its states are unchanged from the
   previous SVG version - only what they act on (a texture, not an SVG
   clip) is different. */
(function () {
  function whenThreeReady(cb) {
    if (window.THREE) { cb(); return; }
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (window.THREE) { clearInterval(timer); cb(); }
      else if (tries > 100) { clearInterval(timer); }
    }, 50);
  }

  function initChart(root) {
    var card = root.closest('.product-size-chart');
    if (!card) return;
    var canvas = card.querySelector('[data-size-chart-canvas]');
    if (!canvas) return;

    var shape = root.dataset.shape;
    var swatchColor = root.dataset.color || '#cbb994';
    var seamFrac = parseFloat(root.dataset.seamFrac || '0'); // 0..1 from top
    var hasLogoRegion = root.dataset.hasLogoRegion === 'true';
    var bandStyle = root.dataset.bandColor || 'black';
    var sectionId = root.dataset.sectionId;

    // A near-white paper color (e.g. #F5F5F5) sits so close to 1.0
    // reflectance that ambient + key + fill + rim light together push it
    // straight to a flat, shadowless blown-out white with no visible
    // shading - it reads as "pure white, no 3D feel" next to the darker
    // colors, which have enough headroom to actually show a highlight/
    // shadow gradient. Nudging only the lightest colors down a bit gives
    // them that same headroom without visibly changing darker colors.
    function paperDisplayColor(hex) {
      var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
      if (!m) return hex;
      var r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
      var lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      // Lighting has since been dialed down separately (it was blowing out
      // every color, not just white), so this only needs a light touch now
      // - a merchant-picked pure #FFFFFF stays recognizably white instead
      // of sliding into visible gray.
      if (lum < 0.86) return hex;
      var t = Math.min((lum - 0.86) / 0.14, 1); // 0 at the threshold, 1 at pure white
      var factor = 1 - t * 0.1; // darken up to ~10% at pure white
      var dr = Math.round(r * factor), dg = Math.round(g * factor), db = Math.round(b * factor);
      return '#' + [dr, dg, db].map(function (v) { return v.toString(16).padStart(2, '0'); }).join('');
    }
    var displaySwatch = paperDisplayColor(swatchColor);
    // The tip (cones) / bottom section below the band (tubes) reads as
    // part of the same continuous shape when it's the exact same color as
    // the body - a slightly darker shade for that region's own default
    // fill (before any logo is uploaded) gives it a visual seam of its
    // own, so it reads as a distinct part rather than blending in.
    function darkenColor(hex, amount) {
      var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
      if (!m) return hex;
      var r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
      var factor = 1 - amount;
      var dr = Math.round(r * factor), dg = Math.round(g * factor), db = Math.round(b * factor);
      return '#' + [dr, dg, db].map(function (v) { return v.toString(16).padStart(2, '0'); }).join('');
    }
    var tipDisplaySwatch = darkenColor(displaySwatch, 0.14);

    var THREE = window.THREE;
    // Slender, illustrative proportions (not literal mm-to-mm scale) - a
    // real tube is roughly 10:1 length:diameter. Went through two rounds
    // of "still too thick" feedback - this is deliberately quite slim now.
    var TOTAL_H = 6;
    var TOP_R = shape === 'cone' ? 0.39 : 0.3;
    var TIP_R = shape === 'cone' ? TOP_R * 0.42 : 0.3;
    var BAND_FRAC_H = 0.13; // fixed decorative band thickness (tubes only)

    // ---- renderer / scene / camera ----
    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    var scene = new THREE.Scene();
    var FOV_DEG = 32;
    var camera = new THREE.PerspectiveCamera(FOV_DEG, 1, 0.1, 100);
    // Camera distance is derived from TOTAL_H (not a hardcoded number) so
    // the full model - point tip and all - always fits inside the frame
    // with real margin, even as TOTAL_H gets tuned later. A fixed z=7.5
    // only had room for ~4.3 units of vertical space, so a 6-unit-tall
    // tube overflowed top and bottom (very visible on a uniform-width
    // tube; disguised on a cone since only its thin point got clipped).
    var halfFovRad = (FOV_DEG * Math.PI / 180) / 2;
    var DEFAULT_Z = (TOTAL_H / 2 * 1.18) / Math.tan(halfFovRad);
    var MIN_Z = DEFAULT_Z * 0.65;
    var MAX_Z = DEFAULT_Z * 1.6;
    camera.position.set(0, 0, DEFAULT_Z);

    // Cutting all the light sources evenly (the earlier "too much lighting"
    // fix) also cut how bright a true white surface can ever read, since
    // there just wasn't enough total light left to reflect - it came out
    // gray no matter what the base color was. The actual glare complaint
    // was about the sharp directional hotspot, not overall brightness, so
    // the fix is to rebalance toward soft ambient fill (even, no hotspot)
    // and keep the directional lights modest, rather than dimming
    // everything together.
    scene.add(new THREE.AmbientLight(0xffffff, 0.75));
    var keyLight = new THREE.DirectionalLight(0xffffff, 0.3);
    keyLight.position.set(3, 4, 5);
    scene.add(keyLight);
    var fillLight = new THREE.DirectionalLight(0xffffff, 0.18);
    fillLight.position.set(-4, -1, 3);
    scene.add(fillLight);
    var rimLight = new THREE.DirectionalLight(0xffffff, 0.12);
    rimLight.position.set(0, 2, -5);
    scene.add(rimLight);

    var group = new THREE.Group();
    scene.add(group);

    // ---- texture helpers ----
    var maxAniso = renderer.capabilities.getMaxAnisotropy();
    function sharpenTexture(tex) {
      tex.anisotropy = maxAniso;
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.generateMipmaps = true;
      return tex;
    }
    // CylinderGeometry's own UV convention puts U=0 (the wrap seam) at
    // theta=0, which lands at +Z - the side already facing the camera at
    // the default rotation. U=0.5, the canvas's own center where the logo
    // is always drawn, lands at -Z - the far side, away from the camera -
    // so an uploaded logo rendered dead center in the canvas was showing
    // up on the model's back instead of its front. Shifting the texture's
    // U origin by half a turn remaps the geometry's front (U=0) to sample
    // the canvas center instead, without touching how the logo is drawn.
    function faceFrontTexture(tex) {
      tex.wrapS = THREE.RepeatWrapping;
      tex.offset.x = 0.5;
      return tex;
    }
    function makeSolidTexture(hex) {
      var c = document.createElement('canvas');
      c.width = 32; c.height = 32;
      var ctx = c.getContext('2d');
      ctx.fillStyle = hex;
      ctx.fillRect(0, 0, 32, 32);
      var tex = new THREE.CanvasTexture(c);
      tex.needsUpdate = true;
      return sharpenTexture(tex);
    }
    // Rolling-paper grain - soft mottled blotches (real paper's uneven
    // pulp/dye absorption) underneath a dense fibrous speckle (random short
    // strokes in every direction), so the paper body reads as an actual
    // matte paper surface rather than flat plastic. Same technique as the
    // Cone Builder studio (assets/cone-builder-studio.js) - kept in sync.
    // Offsets needed so a shape drawn near an edge also gets copies drawn
    // just outside the opposite edge - otherwise tex.repeat tiling shows a
    // hard seam every time the pattern repeats around the cylinder.
    function wrapOffsets(v, size, margin) {
      var offs = [0];
      if (v < margin) offs.push(size);
      if (v > size - margin) offs.push(-size);
      return offs;
    }
    function paintPaperGrain(ctx, size, darkStroke, lightStroke, blotchAlpha, fiberAlpha) {
      var i, j, k, x, y, r, len, ang, ox, oy, oxs, oys;
      for (i = 0; i < 160; i++) {
        x = Math.random() * size;
        y = Math.random() * size;
        r = size * (0.12 + Math.random() * 0.22);
        var shade = Math.random() > 0.5 ? darkStroke : lightStroke;
        oxs = wrapOffsets(x, size, r);
        oys = wrapOffsets(y, size, r);
        for (j = 0; j < oxs.length; j++) {
          for (k = 0; k < oys.length; k++) {
            ox = x + oxs[j];
            oy = y + oys[k];
            var grad = ctx.createRadialGradient(ox, oy, 0, ox, oy, r);
            grad.addColorStop(0, shade);
            grad.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.globalAlpha = blotchAlpha;
            ctx.fillStyle = grad;
            ctx.beginPath();
            ctx.arc(ox, oy, r, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
      for (i = 0; i < 7200; i++) {
        x = Math.random() * size;
        y = Math.random() * size;
        len = size * (0.004 + Math.random() * 0.012);
        ang = Math.random() * Math.PI * 2;
        ctx.strokeStyle = Math.random() > 0.5 ? darkStroke : lightStroke;
        ctx.globalAlpha = fiberAlpha * (0.5 + Math.random());
        ctx.lineWidth = 0.7;
        oxs = wrapOffsets(x, size, len);
        oys = wrapOffsets(y, size, len);
        for (j = 0; j < oxs.length; j++) {
          for (k = 0; k < oys.length; k++) {
            ox = x + oxs[j];
            oy = y + oys[k];
            ctx.beginPath();
            ctx.moveTo(ox, oy);
            ctx.lineTo(ox + Math.cos(ang) * len, oy + Math.sin(ang) * len);
            ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;
    }
    function makePaperTexture(hex) {
      var c = document.createElement('canvas');
      c.width = 512; c.height = 512;
      var ctx = c.getContext('2d');
      ctx.fillStyle = hex;
      ctx.fillRect(0, 0, 512, 512);
      paintPaperGrain(ctx, 512, '#000000', '#ffffff', 0.014, 0.025);

      var tex = new THREE.CanvasTexture(c);
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(1, 1);
      tex.needsUpdate = true;
      return sharpenTexture(tex);
    }
    var paperBumpTextureCache = null;
    function makePaperBumpTexture() {
      if (paperBumpTextureCache) return paperBumpTextureCache;
      var c = document.createElement('canvas');
      c.width = 512; c.height = 512;
      var ctx = c.getContext('2d');
      ctx.fillStyle = '#808080';
      ctx.fillRect(0, 0, 512, 512);
      paintPaperGrain(ctx, 512, '#000000', '#ffffff', 0.1, 0.13);

      var tex = new THREE.CanvasTexture(c);
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(1, 1);
      tex.needsUpdate = true;
      paperBumpTextureCache = sharpenTexture(tex);
      return paperBumpTextureCache;
    }

    function makeLogoCanvas() {
      // High-res backing canvas for the logo/band texture - the previous
      // 512px version was visibly soft once a shopper's logo was drawn
      // onto it and stretched across the mesh's UV space.
      var c = document.createElement('canvas');
      c.width = 1024;
      c.height = 1024;
      return c;
    }

    var bandBaseHex = bandStyle === 'white' ? '#f0f0f0' : '#141414';

    // A thin ring at the shape's true open (top) end, standing in for the
    // paper's own wall thickness - same technique the reference builder
    // uses (a RingGeometry cap, not a solid disc) so the top reads as a
    // real hollow rolled tube/cone instead of a solid capsule.
    function addRimCap(radius, y, hex) {
      var wallT = Math.max(radius * 0.12, 0.02);
      var ringGeo = new THREE.RingGeometry(Math.max(radius - wallT, 0.001), radius, 48);
      var ringMat = new THREE.MeshStandardMaterial({ color: hex, roughness: 0.5, metalness: 0.05, side: THREE.DoubleSide });
      var ring = new THREE.Mesh(ringGeo, ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = y;
      group.add(ring);
    }

    // A thin dark line at a segment boundary (body/tip on cones, body/
    // band/body on tubes) so the pieces read as visually distinct/
    // separate parts, not just an invisible seam in one continuous solid.
    function addSeamRing(radius, y) {
      var torusGeo = new THREE.TorusGeometry(radius, radius * 0.025, 8, 48);
      var torusMat = new THREE.MeshStandardMaterial({ color: 0x000000, roughness: 0.6, metalness: 0.1, transparent: true, opacity: 0.35 });
      var torus = new THREE.Mesh(torusGeo, torusMat);
      torus.rotation.x = Math.PI / 2;
      torus.position.y = y;
      group.add(torus);
    }

    // ---- geometry ----
    var segments = []; // {mesh, canvas?, ctx?, texture, isLogoTarget}
    var logoMesh = null;
    var logoCanvas = null;
    var logoCtx = null;
    // The logo canvas is always a 1024x1024 SQUARE, but it gets UV-wrapped
    // onto a cylindrical surface (tube band / cone tip) whose real-world
    // circumference is usually much wider than it is tall. Drawing the logo
    // at its true pixel aspect ratio on that square canvas, with no
    // correction, makes it come out stretched horizontally once wrapped -
    // this factor (real surface height / real surface circumference) is
    // applied to the logo's drawn width when painting the canvas, set once
    // per build to whichever surface (cone tip or tube band) is active.
    var logoAspectCorrection = 1;

    // The real filter-length ratio (seamFrac) used to drive the cone's tip
    // height 1:1, which read as oversized once the camera-framing fix let
    // shoppers see the whole thing (a 26mm filter on a 109mm cone is really
    // ~24% of the length). The exact mm value is now shown as its own text
    // line ("Filter Length: 26mm"), so the 3D shape no longer has to be a
    // literal ruler - compressing it here keeps a taller filter still
    // reading as taller than a shorter one, just visually slimmer overall,
    // closer to how a real cone tip reads.
    var CONE_TIP_SCALE = 0.85;
    var CONE_TIP_MAX = 0.42;
    var TUBE_TIP_SCALE = 0.45;
    var TUBE_TIP_MAX = 0.36;
    // Real tube filter/length ratios in this catalog run ~0.19-0.36 - below
    // about 0.28 raw, TUBE_TIP_SCALE alone pushes the band low enough to
    // read as sitting too close to the bottom cap. A floor keeps the
    // shortest-filter tubes from going lower than this, at the cost of
    // those tubes no longer visually distinguishing shorter filters from
    // each other below the floor - an acceptable trade for an illustrative
    // diagram, not a literal scale replica.
    var TUBE_TIP_MIN = 0.15;

    function buildCone() {
      var displaySeam = Math.min(seamFrac * CONE_TIP_SCALE, CONE_TIP_MAX);
      var tipH = TOTAL_H * displaySeam;
      var bodyH = TOTAL_H - tipH;
      var midR = TIP_R + (TOP_R - TIP_R) * displaySeam;

      // openEnded on every segment - no solid caps anywhere, matching the
      // reference builder's own approach exactly - plus one explicit thin
      // ring at the true top standing in for the paper's wall thickness.
      // The tip's own bottom stays open too, but its radius is small
      // enough that it reads as a point rather than a visible hole.
      var bodyGeo = new THREE.CylinderGeometry(TOP_R, midR, bodyH, 48, 1, true);
      var bodyMat = new THREE.MeshStandardMaterial({ map: makePaperTexture(displaySwatch), bumpMap: makePaperBumpTexture(), bumpScale: 0.008, roughness: 0.72, metalness: 0.01, transparent: true, opacity: 0.93, side: THREE.FrontSide });
      var bodyMesh = new THREE.Mesh(bodyGeo, bodyMat);
      bodyMesh.position.y = TOTAL_H / 2 - bodyH / 2;
      group.add(bodyMesh);
      addRimCap(TOP_R, TOTAL_H / 2, displaySwatch);

      var tipCircumference = 2 * Math.PI * ((midR + TIP_R) / 2);
      logoAspectCorrection = tipH / tipCircumference;

      logoCanvas = makeLogoCanvas();
      logoCtx = logoCanvas.getContext('2d');
      paintBase(logoCtx, tipDisplaySwatch, true);
      var tipTex = faceFrontTexture(sharpenTexture(new THREE.CanvasTexture(logoCanvas)));
      var tipGeo = new THREE.CylinderGeometry(midR, TIP_R, tipH, 48, 1, true);
      var tipMat = new THREE.MeshStandardMaterial({ map: tipTex, roughness: 0.65, metalness: 0.02, transparent: true, opacity: 0.93, side: THREE.FrontSide });
      logoMesh = new THREE.Mesh(tipGeo, tipMat);
      logoMesh.position.y = TOTAL_H / 2 - bodyH - tipH / 2;
      group.add(logoMesh);
      addSeamRing(midR, TOTAL_H / 2 - bodyH);
    }

    function buildTube() {
      if (!hasLogoRegion) {
        var geo = new THREE.CylinderGeometry(TOP_R, TOP_R, TOTAL_H, 48, 1, true);
        var mat = new THREE.MeshStandardMaterial({ map: makePaperTexture(displaySwatch), bumpMap: makePaperBumpTexture(), bumpScale: 0.008, roughness: 0.72, metalness: 0.01, transparent: true, opacity: 0.93, side: THREE.FrontSide });
        var mesh = new THREE.Mesh(geo, mat);
        group.add(mesh);
        addRimCap(TOP_R, TOTAL_H / 2, displaySwatch);
        return;
      }
      var bandH = TOTAL_H * BAND_FRAC_H;
      // The tube reads as 3 parts: top, band, then the "tip" (the bottom
      // part below the band, standing in for the filter/mouthpiece
      // section). Its height is TOTAL_H - topH - bandH, driven by where
      // the band sits - which was the raw filter_length/length ratio
      // (~27% for a 30mm filter on a 109mm tube), making that bottom
      // "tip" segment read as oversized once fully visible. Same fix as
      // the cone tip: compress the ratio for display, the exact mm value
      // is already shown as its own text line.
      // Tubes get their own multiplier on top of the shared cone/tube
      // compression - raising the whole band+tip cluster higher up the
      // tube grows the tip and shrinks the top part at the same time.
      var displaySeamTube = Math.max(Math.min(seamFrac * TUBE_TIP_SCALE, TUBE_TIP_MAX), TUBE_TIP_MIN);
      // seamFrac is measured from the BOTTOM (it's filter_length / length),
      // so the boundary's world Y (where +Y is up, origin at vertical
      // center) is TOTAL_H*seamFrac above the bottom edge (-TOTAL_H/2) -
      // i.e. -TOTAL_H/2 + TOTAL_H*seamFrac. An earlier version had this
      // backwards (TOTAL_H/2 - TOTAL_H*seamFrac), putting the band above
      // center instead of below it - "showing inverse".
      var seamY = TOTAL_H * displaySeamTube - TOTAL_H / 2;
      var bandTopY = seamY + bandH / 2;
      var topH = TOTAL_H / 2 - bandTopY;
      var bottomH = TOTAL_H - topH - bandH;

      var topGeo = new THREE.CylinderGeometry(TOP_R, TOP_R, Math.max(topH, 0.001), 48, 1, true);
      var topMat = new THREE.MeshStandardMaterial({ map: makePaperTexture(displaySwatch), bumpMap: makePaperBumpTexture(), bumpScale: 0.008, roughness: 0.72, metalness: 0.01, transparent: true, opacity: 0.93, side: THREE.FrontSide });
      var topMesh = new THREE.Mesh(topGeo, topMat);
      topMesh.position.y = TOTAL_H / 2 - topH / 2;
      group.add(topMesh);
      addRimCap(TOP_R, TOTAL_H / 2, displaySwatch);

      var bandCircumference = 2 * Math.PI * TOP_R;
      logoAspectCorrection = bandH / bandCircumference;

      logoCanvas = makeLogoCanvas();
      logoCtx = logoCanvas.getContext('2d');
      paintBase(logoCtx, bandBaseHex);
      var bandTex = faceFrontTexture(sharpenTexture(new THREE.CanvasTexture(logoCanvas)));
      var bandGeo = new THREE.CylinderGeometry(TOP_R, TOP_R, bandH, 48, 1, true);
      var bandMat = new THREE.MeshStandardMaterial({ map: bandTex, roughness: 0.3, metalness: 0.15, side: THREE.DoubleSide });
      logoMesh = new THREE.Mesh(bandGeo, bandMat);
      logoMesh.position.y = bandTopY - bandH / 2;
      group.add(logoMesh);
      addSeamRing(TOP_R, bandTopY);
      addSeamRing(TOP_R, bandTopY - bandH);

      var bottomGeo = new THREE.CylinderGeometry(TOP_R, TOP_R, Math.max(bottomH, 0.001), 48, 1, true);
      var bottomMat = new THREE.MeshStandardMaterial({ map: makePaperTexture(tipDisplaySwatch), bumpMap: makePaperBumpTexture(), bumpScale: 0.008, roughness: 0.72, metalness: 0.01, transparent: true, opacity: 0.93, side: THREE.FrontSide });
      var bottomMesh = new THREE.Mesh(bottomGeo, bottomMat);
      bottomMesh.position.y = -TOTAL_H / 2 + bottomH / 2;
      group.add(bottomMesh);
    }

    function paintBase(ctx, hex, withGrain) {
      ctx.clearRect(0, 0, logoCanvas.width, logoCanvas.height);
      ctx.fillStyle = hex;
      ctx.fillRect(0, 0, logoCanvas.width, logoCanvas.height);
      if (withGrain) {
        paintPaperGrain(ctx, logoCanvas.width, '#000000', '#ffffff', 0.03, 0.05);
      }
    }

    if (shape === 'cone') buildCone();
    else buildTube();

    function refreshLogoTexture() {
      if (logoMesh) logoMesh.material.map.needsUpdate = true;
    }

    // ---- sizing ----
    // The size chart is the LAST gallery slide, hidden (display:none) until
    // a shopper clicks it - at that point clientWidth/Height read as 0, so
    // a plain resize() at init leaves the renderer stuck forever at Three's
    // tiny 300x150 default internal resolution (upscaled by CSS to fill the
    // 320x460 box), which reads as "blurry" no matter how sharp the
    // textures are. ResizeObserver fires again the moment the slide
    // actually becomes visible and gets its real size, so this self-heals
    // instead of needing a "slide changed" hook into the gallery code.
    function resize() {
      var w = canvas.clientWidth || canvas.parentElement.clientWidth;
      var h = canvas.clientHeight || canvas.parentElement.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    window.addEventListener('resize', resize);
    resize();
    if (window.ResizeObserver) {
      new ResizeObserver(resize).observe(root);
    }

    // ---- drag to rotate / scroll to zoom ----
    var dragging = false;
    var lastX = 0, lastY = 0;
    var centerIcon = card.querySelector('[data-size-chart-center-icon]');

    function pointerDown(e) {
      dragging = true;
      var p = e.touches ? e.touches[0] : e;
      lastX = p.clientX;
      lastY = p.clientY;
      if (centerIcon) centerIcon.classList.add('is-visible');
      canvas.style.cursor = 'grabbing';
    }
    function pointerMove(e) {
      if (!dragging) return;
      var p = e.touches ? e.touches[0] : e;
      var dx = p.clientX - lastX;
      var dy = p.clientY - lastY;
      lastX = p.clientX;
      lastY = p.clientY;
      group.rotation.y += dx * 0.012;
      group.rotation.x = Math.max(-0.5, Math.min(0.5, group.rotation.x + dy * 0.008));
      e.preventDefault();
    }
    function pointerUp() {
      dragging = false;
      if (centerIcon) centerIcon.classList.remove('is-visible');
      canvas.style.cursor = 'grab';
    }
    canvas.addEventListener('mousedown', pointerDown);
    window.addEventListener('mousemove', pointerMove);
    window.addEventListener('mouseup', pointerUp);
    canvas.addEventListener('touchstart', pointerDown, { passive: true });
    canvas.addEventListener('touchmove', pointerMove, { passive: false });
    canvas.addEventListener('touchend', pointerUp);

    canvas.addEventListener('wheel', function (e) {
      e.preventDefault();
      camera.position.z = Math.max(MIN_Z, Math.min(MAX_Z, camera.position.z + e.deltaY * 0.01));
    }, { passive: false });

    canvas.style.cursor = 'grab';

    // ---- render loop ----
    var thumbImg = sectionId ? document.querySelector('[data-size-chart-thumb-img="' + sectionId + '"]') : null;
    var thumbTaken = false;
    function animate() {
      requestAnimationFrame(animate);
      // No idle auto-rotation - the model stays put until the shopper
      // actually drags it, rather than spinning on its own.
      renderer.render(scene, camera);
      if (!thumbTaken && thumbImg) {
        thumbTaken = true;
        setTimeout(function () {
          try {
            // The size chart is the LAST gallery slide, hidden
            // (display:none) until a shopper opens it - but this snapshot
            // fires automatically right away, long before that. At this
            // point clientWidth/Height are 0, so the renderer is still
            // stuck at Three's tiny 300x150 default canvas (a wide 2:1
            // box), not the real portrait ~320x460 diagram shape - the
            // model then reads as short and squashed in the thumbnail
            // rail. Force a real portrait buffer/aspect for just this one
            // capture frame (the ResizeObserver already handles sizing
            // correctly once the shopper actually opens the slide), then
            // restore whatever resize() had before.
            // 0.68 here used to clip the model top+bottom: DEFAULT_Z
            // already only has ~1.18x vertical margin over the model's
            // own half-height, so multiplying it by anything below
            // ~0.85 (1 / 1.18) shrinks the visible frame smaller than
            // the model itself. 0.92 keeps the "zoomed in a bit more
            // than the live view" intent with real clearance left over.
            var liveZ = camera.position.z;
            var liveAspect = camera.aspect;
            var liveW = canvas.width, liveH = canvas.height;
            renderer.setSize(400, 575, false);
            camera.aspect = 400 / 575;
            camera.updateProjectionMatrix();
            camera.position.z = DEFAULT_Z * 0.92;
            camera.updateProjectionMatrix();
            renderer.render(scene, camera);
            thumbImg.src = renderer.domElement.toDataURL('image/png');
            camera.position.z = liveZ;
            camera.aspect = liveAspect;
            camera.updateProjectionMatrix();
            renderer.setSize(liveW, liveH, false);
            renderer.render(scene, camera);
          } catch (e) {}
        }, 150);
      }
    }
    animate();

    // Expose what the logo controller (below) needs.
    card.__sizeChart3D = {
      shape: shape,
      logoCanvas: logoCanvas,
      logoCtx: logoCtx,
      logoAspectCorrection: logoAspectCorrection,
      swatchColor: tipDisplaySwatch,
      bandBaseHex: bandBaseHex,
      refreshLogoTexture: refreshLogoTexture,
      renderer: renderer,
      scene: scene,
      camera: camera
    };
  }

  function initLogoControls(root) {
    var card = root.closest('.product-size-chart');
    if (!card) return;

    var bandColorRow = root.querySelector('[data-band-color-row]');
    var uploadRow = root.querySelector('[data-logo-upload-row]');
    var fileInput = root.querySelector('[data-logo-file-input]');
    var orientationRow = root.querySelector('[data-logo-orientation-row]');
    var toolbar = root.querySelector('[data-logo-toolbar]');
    var removeBtn = root.querySelector('[data-logo-remove]');
    var sizeInput = root.querySelector('[data-logo-size]');
    var exportBtn = root.querySelector('[data-logo-export]');
    var backBtn = toolbar ? toolbar.querySelector('[data-logo-back]') : null;

    // Same naming as the Cone Builder Studio export - shape + real date/
    // time instead of a static "cone-logo-mockup.jpg" every single time,
    // so a shopper downloading more than one design doesn't get an
    // overwritten/ambiguous file each time.
    function timestampedFilename(shape) {
      var d = new Date();
      var pad = function (n) { return String(n).padStart(2, '0'); };
      var stamp = d.getFullYear() + pad(d.getMonth() + 1) + pad(d.getDate()) + '-' + pad(d.getHours()) + pad(d.getMinutes()) + pad(d.getSeconds());
      return shape + '-export-' + stamp + '.jpg';
    }

    var currentImage = null;
    var currentOrientation = 'horizontal';

    function chart3d() { return card.__sizeChart3D; }

    function showState(state) {
      if (uploadRow) uploadRow.hidden = state !== 'upload';
      if (orientationRow) orientationRow.hidden = state !== 'orientation';
      if (toolbar) toolbar.hidden = state !== 'toolbar';
    }

    function render() {
      var c3 = chart3d();
      if (!c3 || !currentImage) return;
      var ctx = c3.logoCtx;
      var canvasEl = c3.logoCanvas;
      var W = canvasEl.width, H = canvasEl.height;

      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = c3.shape === 'tube' ? c3.bandBaseHex : c3.swatchColor;
      ctx.fillRect(0, 0, W, H);

      var scalePct = sizeInput ? parseFloat(sizeInput.value) : 100;
      var srcW = currentImage.naturalWidth || currentImage.width;
      var srcH = currentImage.naturalHeight || currentImage.height;
      var rotate = currentOrientation === 'vertical';
      var effW = rotate ? srcH : srcW;
      var effH = rotate ? srcW : srcH;

      // Real 3D geometry does the "wrapped around a cylinder" perspective
      // automatically once rendered - no manual per-row taper warp needed
      // here the way the old flat-SVG version required. Just fill the
      // available texture area (fill-by-height, matching the "as big as
      // possible" sizing already established), the mesh curvature and
      // camera do the rest.
      var scale = (H / effH) * (scalePct / 100);
      // drawW always ends up spanning the canvas's horizontal (circumference)
      // axis in the final orientation - even when rotated, since effW/effH
      // are already swapped for that case above - so this is the one place
      // that needs the aspect correction (see logoAspectCorrection comment
      // near its declaration above).
      var drawW = effW * scale * (c3.logoAspectCorrection || 1);
      var drawH = effH * scale;

      // When rotated, the drawImage call's own width/height args are
      // swapped (drawH,drawW not drawW,drawH) - drawW/drawH already
      // account for the source-dimension swap via effW/effH above, and
      // the rotate(-90deg) transform then rotates that placement into the
      // final orientation. Getting this backwards silently squashes the
      // logo instead of rotating it cleanly.
      ctx.save();
      ctx.translate(W / 2, H / 2);
      if (rotate) {
        ctx.rotate(-Math.PI / 2);
        ctx.drawImage(currentImage, -drawH / 2, -drawW / 2, drawH, drawW);
      } else {
        ctx.drawImage(currentImage, -drawW / 2, -drawH / 2, drawW, drawH);
      }
      ctx.restore();

      c3.refreshLogoTexture();
    }

    // Band color is now a shopper-facing choice on the product page itself
    // (black/white pill buttons), not a merchant-only theme-editor setting
    // - the section setting still supplies the initial default, but the
    // shopper can change it live here at any point, logo uploaded or not.
    if (bandColorRow) {
      bandColorRow.querySelectorAll('[data-band-color-choice]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var c3 = chart3d();
          if (!c3) return;
          var hex = btn.dataset.bandColorChoice === 'white' ? '#f0f0f0' : '#141414';
          c3.bandBaseHex = hex;
          bandColorRow.querySelectorAll('[data-band-color-choice]').forEach(function (b) {
            b.classList.toggle('is-active', b === btn);
          });
          if (currentImage) {
            render();
          } else {
            var ctx = c3.logoCtx, canvasEl = c3.logoCanvas;
            ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
            ctx.fillStyle = hex;
            ctx.fillRect(0, 0, canvasEl.width, canvasEl.height);
            c3.refreshLogoTexture();
          }
        });
      });
    }

    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var file = fileInput.files && fileInput.files[0];
        if (!file || file.type.indexOf('image/') !== 0) return;
        var reader = new FileReader();
        reader.onload = function (e) {
          var img = new Image();
          img.onload = function () {
            currentImage = img;
            // Show on the model immediately, at the default orientation,
            // instead of waiting for the user to click a Horizontal/
            // Vertical button first - those buttons just let them change
            // it afterward, matching the Cone Builder Studio's behavior.
            render();
            showState('orientation');
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
    }

    if (orientationRow) {
      orientationRow.querySelectorAll('[data-orientation]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          currentOrientation = btn.dataset.orientation;
          if (sizeInput) sizeInput.value = 100;
          render();
          showState('toolbar');
        });
      });
    }

    if (sizeInput) sizeInput.addEventListener('input', render);

    if (backBtn) {
      backBtn.addEventListener('click', function () { showState('orientation'); });
    }

    if (removeBtn) {
      removeBtn.addEventListener('click', function () {
        currentImage = null;
        var c3 = chart3d();
        if (c3) {
          var ctx = c3.logoCtx, canvasEl = c3.logoCanvas;
          ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
          ctx.fillStyle = c3.shape === 'tube' ? c3.bandBaseHex : c3.swatchColor;
          ctx.fillRect(0, 0, canvasEl.width, canvasEl.height);
          c3.refreshLogoTexture();
        }
        if (fileInput) fileInput.value = '';
        showState('upload');
      });
    }

    function loadImagePromise(src, allowCrossOrigin) {
      return new Promise(function (resolve) {
        var img = new Image();
        if (allowCrossOrigin) img.crossOrigin = 'anonymous';
        img.onload = function () { resolve(img); };
        img.onerror = function () { resolve(null); };
        img.src = src;
      });
    }

    function tileWatermark(ctx, canvasSize, watermarkImg) {
      ctx.save();
      ctx.translate(canvasSize / 2, canvasSize / 2);
      ctx.rotate(-Math.PI / 8);
      ctx.translate(-canvasSize, -canvasSize);
      var span = canvasSize * 3;
      if (watermarkImg) {
        ctx.globalAlpha = 0.07;
        var tileW = 140;
        var tileH = tileW * (watermarkImg.naturalHeight / watermarkImg.naturalWidth || 0.4);
        var stepX = tileW + 70;
        var stepY = tileH + 70;
        for (var y = 0; y < span; y += stepY) {
          for (var x = 0; x < span; x += stepX) ctx.drawImage(watermarkImg, x, y, tileW, tileH);
        }
      } else {
        ctx.globalAlpha = 0.06;
        ctx.fillStyle = '#121212';
        ctx.font = '700 20px Arial, Helvetica, sans-serif';
        ctx.textAlign = 'center';
        for (var yt = 0; yt < span; yt += 90) {
          for (var xt = 0; xt < span; xt += 240) ctx.fillText('KING CUSTOM CONES', xt, yt);
        }
      }
      ctx.restore();
    }

    if (exportBtn) {
      exportBtn.addEventListener('click', function () {
        var c3 = chart3d();
        if (!c3) return;
        c3.renderer.render(c3.scene, c3.camera);
        var glCanvas = c3.renderer.domElement;

        var labelBlock = card.querySelector('.product-size-chart__label-block');
        var nameText = labelBlock ? (labelBlock.querySelector('.product-size-chart__name') || {}).textContent : '';
        var capacityText = labelBlock ? (labelBlock.querySelector('.product-size-chart__capacity') || {}).textContent : '';
        var mmText = labelBlock ? (labelBlock.querySelector('.product-size-chart__mm') || {}).textContent : '';
        var tipMmText = labelBlock ? (labelBlock.querySelector('.product-size-chart__tip-mm') || {}).textContent : '';

        var logoImgEl = document.querySelector('.header__heading-logo img, a.header__heading-logo-link img, header img[src*="logo" i]');
        var watermarkSrc = logoImgEl ? logoImgEl.currentSrc || logoImgEl.src : null;

        // Same real dimension data shown on the page itself (the fixed
        // green dimension lines) - read straight from those DOM labels so
        // the export doesn't need its own separate source of truth. These
        // live outside the 3D canvas (plain HTML/SVG), so they were never
        // part of glCanvas and need to be redrawn onto the export
        // separately, not just copied along with it.
        var dimLabelEls = card.querySelectorAll('.product-size-chart__dim-label');
        var topDimText = dimLabelEls[0] ? dimLabelEls[0].textContent.trim() : '';
        var bottomDimText = dimLabelEls[1] ? dimLabelEls[1].textContent.trim() : '';
        var diaLabelEl = card.querySelector('.product-size-chart__dim-h-label');
        var diaText = diaLabelEl ? diaLabelEl.textContent.trim() : '';
        function parseMM(text) {
          var m = /(\d+(?:\.\d+)?)/.exec(text || '');
          return m ? parseFloat(m[1]) : null;
        }
        var topNum = parseMM(topDimText), bottomNum = parseMM(bottomDimText);
        var dimSplitFrac = (topNum != null && bottomNum != null && (topNum + bottomNum) > 0)
          ? topNum / (topNum + bottomNum) : 0.7;
        var hasVerticalDim = !!(topDimText && bottomDimText);
        var hasHorizontalDim = !!diaText;

        (watermarkSrc ? loadImagePromise(watermarkSrc, true) : Promise.resolve(null)).then(function (watermarkImg) {
          var canvasSize = 1000;
          var padding = 70;
          var labelAreaH = 140;
          // Reserve dedicated space for the dimension lines so they never
          // overlap the diagram image itself, same principle as the fixed
          // on-page layout (stacked in normal flow, not overlaid on the
          // canvas).
          var leftDimSpace = hasVerticalDim ? 66 : 0;
          var topDimSpace = hasHorizontalDim ? 50 : 0;
          var diagramMaxW = canvasSize - padding * 2 - leftDimSpace;
          var diagramMaxH = canvasSize - padding * 2 - labelAreaH - topDimSpace;
          var glAspect = glCanvas.width / glCanvas.height;
          var diagramW = diagramMaxW;
          var diagramH = diagramW / glAspect;
          if (diagramH > diagramMaxH) {
            diagramH = diagramMaxH;
            diagramW = diagramH * glAspect;
          }

          var outCanvas = document.createElement('canvas');
          outCanvas.width = canvasSize;
          outCanvas.height = canvasSize;
          var ctx = outCanvas.getContext('2d');
          ctx.fillStyle = '#eeeeec';
          ctx.fillRect(0, 0, canvasSize, canvasSize);

          var showWatermark = !(window.theme && window.theme.coneBuilderSettings && window.theme.coneBuilderSettings.showWatermark === false);
          if (showWatermark) {
            try { tileWatermark(ctx, canvasSize, watermarkImg); } catch (e) {}
          }

          var dx = (canvasSize - diagramW + leftDimSpace) / 2;
          var dy = padding + topDimSpace;
          ctx.drawImage(glCanvas, dx, dy, diagramW, diagramH);

          // Redraw the same green dimension-line style used on the page
          // itself directly onto the export canvas.
          ctx.strokeStyle = '#3ecf8e';
          ctx.fillStyle = '#2fae78';
          ctx.lineWidth = 2;
          ctx.font = '700 15px Arial, Helvetica, sans-serif';
          if (hasVerticalDim) {
            var lineX = dx - 30;
            var lineTopY = dy + 6, lineBottomY = dy + diagramH - 6;
            var lineMidY = lineTopY + (lineBottomY - lineTopY) * dimSplitFrac;
            ctx.beginPath();
            ctx.moveTo(lineX, lineTopY); ctx.lineTo(lineX, lineBottomY);
            [lineTopY, lineMidY, lineBottomY].forEach(function (y) {
              ctx.moveTo(lineX - 6, y); ctx.lineTo(lineX + 6, y);
            });
            ctx.stroke();
            ctx.textAlign = 'right';
            ctx.fillText(topDimText, lineX - 10, (lineTopY + lineMidY) / 2 + 5);
            ctx.fillText(bottomDimText, lineX - 10, (lineMidY + lineBottomY) / 2 + 5);
          }
          if (hasHorizontalDim) {
            var hLineY = dy - 26;
            var hLineX1 = dx + diagramW * 0.2, hLineX2 = dx + diagramW * 0.8;
            ctx.beginPath();
            ctx.moveTo(hLineX1, hLineY); ctx.lineTo(hLineX2, hLineY);
            ctx.moveTo(hLineX1, hLineY - 6); ctx.lineTo(hLineX1, hLineY + 6);
            ctx.moveTo(hLineX2, hLineY - 6); ctx.lineTo(hLineX2, hLineY + 6);
            ctx.stroke();
            ctx.textAlign = 'center';
            ctx.fillText(diaText, (hLineX1 + hLineX2) / 2, hLineY - 12);
          }

          var textY = dy + diagramH + 46;
          ctx.textAlign = 'center';
          if (nameText) {
            ctx.fillStyle = '#121212';
            ctx.font = '700 26px Arial, Helvetica, sans-serif';
            ctx.fillText(nameText.trim(), canvasSize / 2, textY);
            textY += 32;
          }
          if (capacityText) {
            ctx.fillStyle = '#3ecf8e';
            ctx.font = '600 22px Arial, Helvetica, sans-serif';
            ctx.fillText(capacityText.trim(), canvasSize / 2, textY);
            textY += 28;
          }
          if (mmText) {
            ctx.fillStyle = '#121212';
            ctx.font = '400 18px Arial, Helvetica, sans-serif';
            ctx.fillText(mmText.trim(), canvasSize / 2, textY);
            textY += 24;
          }
          if (tipMmText) {
            ctx.fillStyle = 'rgba(18, 18, 18, 0.65)';
            ctx.font = '400 16px Arial, Helvetica, sans-serif';
            ctx.fillText(tipMmText.trim(), canvasSize / 2, textY);
          }

          var jpgUrl;
          try {
            jpgUrl = outCanvas.toDataURL('image/jpeg', 0.92);
          } catch (e) {
            var clean = document.createElement('canvas');
            clean.width = canvasSize; clean.height = canvasSize;
            var cctx = clean.getContext('2d');
            cctx.fillStyle = '#eeeeec';
            cctx.fillRect(0, 0, canvasSize, canvasSize);
            cctx.drawImage(glCanvas, dx, dy, diagramW, diagramH);
            jpgUrl = clean.toDataURL('image/jpeg', 0.92);
          }

          var a = document.createElement('a');
          a.href = jpgUrl;
          a.download = timestampedFilename(c3.shape);
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        });
      });
    }

    showState('upload');
  }

  whenThreeReady(function () {
    document.querySelectorAll('[data-size-chart-3d]').forEach(initChart);
    document.querySelectorAll('[data-logo-controls]').forEach(initLogoControls);
  });
})();
